import React from 'react';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SvgIconComponent } from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  DEPLOYER_URL,
  NODE,
  TEST_LANES,
  WORKFLOW_NODES_BY_ID,
  WorkflowNodeKind,
  pathRuleText,
} from '../../../workflow/workflowConfig';
import deployerBird from '../../../assets/deployer-bird.svg';

interface WorkflowHelpProps {
  // The currently selected stage id, or null when nothing is selected.
  stage: string | null;
  // Selects another stage from an in-app stage link in the help (e.g. a "needs" queue linking to the
  // lane's glossary stage). Omitted when the help is rendered outside the stage-selection context.
  onSelectStage?: (stage: string) => void;
}

// Render a help paragraph, turning **term** spans into <strong>. Config stays plain strings (so it is
// trivially testable and JSX-free); the bolding happens only at render time. Even-index parts are
// plain text, odd-index parts are the captured bold terms.
function renderRichText(text: string): React.ReactNode {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

// Below this window height the panel is too cramped to spend horizontal space on centering, so the
// help fills the full panel width instead of the reading-width column used on taller windows.
const READING_WIDTH_MIN_HEIGHT = 800;
const READING_WIDTH = 700;

type Accent = 'primary' | 'success' | 'warning';

// The title icon per stage kind, mirroring the diagram's per-kind accents so a stage's help reads as
// the same "kind" as its diagram node. Failure side-states and Merged are special-cased at the call site.
const KIND_ICON: Record<WorkflowNodeKind, SvgIconComponent> = {
  parked: PauseCircleOutlineIcon,
  group: PauseCircleOutlineIcon,
  review: RateReviewOutlinedIcon,
  terminal: CheckCircleOutlineIcon,
  unit: ScienceOutlinedIcon,
  test: BiotechOutlinedIcon,
};
const KIND_ICON_COLOR: Record<WorkflowNodeKind, string> = {
  parked: 'text.secondary',
  group: 'text.secondary',
  review: 'primary.main',
  terminal: 'success.main',
  unit: 'warning.main',
  test: 'info.main',
};

// A tinted circular badge holding a step number, matching the diagram's per-kind accent tints.
const NumberBadge: React.FC<{ n: number; accent: Accent }> = ({ n, accent }) => (
  <Box
    sx={{
      flexShrink: 0,
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: theme => alpha(theme.palette[accent].main, 0.15),
      color: accent + '.main',
      fontWeight: 700,
      fontSize: 13,
    }}
  >
    {n}
  </Box>
);

// One numbered "how it works" row: a badge on the left, a bold title and explanation on the right.
const InfoItem: React.FC<{ badge: React.ReactNode; title: string; children: React.ReactNode }> = ({
  badge,
  title,
  children,
}) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    {badge}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  </Box>
);

// The muted, italic caption style used by the panel's navigation hint.
const CAPTION_SX = { fontStyle: 'italic' } as const;

// Always pinned to the top-left of the panel (not in the centered reading column): the table is to
// the left of this panel, the diagram is above it.
const PanelHint: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: 0.5,
      rowGap: 0.25,
      color: 'text.secondary',
      flexShrink: 0,
    }}
  >
    <ArrowBackIcon sx={{ fontSize: 16 }} />
    <Typography variant="caption" sx={CAPTION_SX}>
      Choose a PR from the table to show details, or
    </Typography>
    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
    <Typography variant="caption" sx={CAPTION_SX}>
      Click a stage in the diagram to filter the table
    </Typography>
  </Box>
);

// Fills the panel: hint at top-left, then a nested flex so margin:auto can still center the reading
// column in the leftover space without dragging the hint along.
const HelpShell: React.FC<{ gap: number; children: React.ReactNode }> = ({ gap, children }) => {
  // On a tall enough window, constrain the help to a centered reading column; on short windows use
  // the full panel width so nothing is wasted.
  const useReadingWidth = useMediaQuery('(min-height:' + READING_WIDTH_MIN_HEIGHT + 'px)');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: '100%' }}>
      <PanelHint />
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: useReadingWidth ? READING_WIDTH : 'none',
            m: 'auto',
            gap,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// Fills the detail panel when no PR is selected. With a stage selected it explains that stage (the
// text the diagram nodes used to carry as tooltips) plus, for the test lanes, the path rules that
// trigger them and links to the test source; with nothing selected it gives a tour of the page.
const WorkflowHelp: React.FC<WorkflowHelpProps> = ({ stage, onSelectStage }) => {
  const def = stage ? WORKFLOW_NODES_BY_ID[stage] : undefined;

  if (def) {
    const isMerged = def.id === NODE.merged;
    const TitleIcon = def.failure ? ErrorOutlineIcon : KIND_ICON[def.kind];
    const titleIconColor = def.failure ? 'warning.main' : KIND_ICON_COLOR[def.kind];
    return (
      <HelpShell gap={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMerged ? (
            <Box component="img" src={deployerBird} alt="" sx={{ width: 22, height: 22 }} />
          ) : (
            <TitleIcon sx={{ color: titleIconColor }} />
          )}
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{def.label}</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">{def.description}</Typography>
        {def.details?.map(paragraph => (
          <Typography key={paragraph} variant="body2" color="text.secondary">{renderRichText(paragraph)}</Typography>
        ))}
        {def.pathRules && def.pathRules.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Required when a changed file matches:
            </Typography>
            <Box
              component="pre"
              data-testid="path-rules"
              sx={{
                m: 0,
                p: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: 12,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {def.pathRules.map(pathRuleText).join('\n')}
            </Box>
          </Box>
        )}
        {def.links && def.links.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
            {def.links.map(link => (
              <Link key={link.href} href={link.href} target="_blank" rel="noopener" variant="body2">
                {link.label}
              </Link>
            ))}
          </Box>
        )}
        {onSelectStage && def.stageLinks && def.stageLinks.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
            {def.stageLinks.map(stageId => {
              const target = WORKFLOW_NODES_BY_ID[stageId];
              if (!target) return null;
              return (
                <Link
                  key={stageId}
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => onSelectStage(stageId)}
                >
                  {target.label} →
                </Link>
              );
            })}
          </Box>
        )}
      </HelpShell>
    );
  }

  // Names of the scoped lanes, sourced from TEST_LANES so the copy tracks the actual lanes.
  const laneNames = TEST_LANES.map(lane => lane.label).join(' and ');
  // Lanes whose finished runs need a human to inspect and accept the results before they count as
  // green (recompute today), so the overview can name them without hard-coding the label.
  const verifyLaneNames = TEST_LANES.filter(lane => lane.verify).map(lane => lane.label).join(' and ');

  return (
    <HelpShell gap={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTreeIcon color="primary" />
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Pull Request Workflow</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Track every open PR through its lifecycle, from initial draft through checks to merge.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
        <InfoItem badge={<NumberBadge n={1} accent="primary" />} title="Progressive stages">
          PRs flow left to right: Parked (drafts or held) into Open, then Reviewing (with Changes
          requested and Merge conflicts as blockers hanging off it), Ready, and finally Merged. An
          approval isn't a stop of its own — an approved PR moves straight on toward Ready.
        </InfoItem>

        <InfoItem badge={<NumberBadge n={2} accent="success" />} title="Parallel quality gates">
          Unit tests run on GitHub for every PR. The {laneNames} lanes are scoped — each is only
          required when a changed file matches its path rules. Every required lane must be green
          before a PR reaches Ready.{verifyLaneNames && ' A finished ' + verifyLaneNames + ' run is not automatically green: a person inspects its diffs and error logs and marks it verified first.'}
        </InfoItem>

        <InfoItem badge={<NumberBadge n={3} accent="warning" />} title="Dashboard controls">
          Click any stage in the diagram to filter the table below. Click the active stage again (or
          the background) to clear it. Select a PR row to inspect its details, including each test
          lane.
        </InfoItem>

        <InfoItem
          badge={<Box component="img" src={deployerBird} alt="" sx={{ width: 28, height: 28, flexShrink: 0 }} />}
          title="After merge"
        >
          Merged changes sit on main until the{' '}
          <Link href={DEPLOYER_URL} target="_blank" rel="noopener">Deployer</Link>{' '}
          promotes them to the release branch and rolls them out.
        </InfoItem>
      </Box>
    </HelpShell>
  );
};

export default WorkflowHelp;
